import { Controller } from "@hotwired/stimulus"

// "Wer zahlt was?"-Check für die außerklinische Intensivpflege.
//
// Kein Kostenvergleich: Die Behandlungspflege trägt in allen drei Versorgungsformen
// die Krankenkasse (§ 37c SGB V). Groß steht nur EINE Zahl, die Zuzahlung für die
// Pflege; die Pflegekassen-Leistungen fließen in die Gegenrichtung und stehen
// kleiner daneben. Wohnen bleibt unbeziffert (für WG-Mieten gibt es keine Erhebung).
//
// Rechtlich heikle Stellen:
// - Vollstationär trägt die Krankenkasse auch Investitionskosten, Unterkunft und
//   Verpflegung (§ 37c Abs. 3 S. 1 SGB V) — nie vdek-Heimwerte anzeigen.
// - Pflegegeld, Entlastungsbetrag und Verhinderungspflege setzen häusliche Pflege
//   voraus und entfallen vollstationär; dort greift allein der § 43-Betrag.
// - Zuzahlung (§ 37c Abs. 5, § 61 SGB V): in Heim und Intensiv-WG pauschal je
//   Kalendertag (§ 61 S. 2), zu Hause Prozent der Kosten plus Betrag je Verordnung
//   (§ 61 S. 3). Beide höchstens für die ersten 28 Kalendertage, ab 18 Jahren.
// Die Beträge kommen aus dem Controller (dataValue) und der Seite (formelValue).
// Das Startszenario rendert der Server vor (IntensivpflegeCheck.start), hier nur Updates.
export default class extends Controller {
  static values = { data: Object, formel: Object }

  static targets = [
    "ortButton", "pflegegradButton", "angehoerigeButton", "angehoerigeBlock",
    "heroValue", "heroUnit", "heroSub", "pkFigure", "kkExtra",
    "geldRow", "geldLabel", "geldParagraph", "geldAmount",
    "verhinderungRow", "verhinderungAmount",
    "entlastungRow", "entlastungAmount",
    "wgZuschlagRow", "wgZuschlagAmount",
    "stationaerRow", "stationaerAmount",
    "keinAnspruchRow", "pkSumme", "pkHeading", "pkUnit",
    "selbstText", "selbstNote",
    "stmtKk", "stmtPk", "stmtSelbst",
    "stickyBar", "stickyValue"
  ]

  connect() {
    this.euro = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
    this.ort = "home"
    this.pflegegrad = 4
    this.angehoerige = true
    this.render()
    this.observeCtas()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  selectOrt(event) {
    this.ort = event.currentTarget.dataset.value
    this.render()
  }

  selectPflegegrad(event) {
    this.pflegegrad = parseInt(event.currentTarget.dataset.value)
    this.render()
  }

  selectAngehoerige(event) {
    this.angehoerige = event.currentTarget.dataset.value === "ja"
    this.render()
  }

  // Phone sticky bar: sichtbar, solange kein Anbieter-Button (Hero, Ergebnis) im Bild ist
  observeCtas() {
    if (!this.hasStickyBarTarget || !("IntersectionObserver" in window)) return
    const ctas = document.querySelectorAll("[data-intensivpflege-cta]")
    if (ctas.length === 0) return
    const visible = new Set()
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target))
      const show = visible.size === 0
      this.stickyBarTarget.classList.toggle("translate-y-full", !show)
      this.stickyBarTarget.inert = !show
    })
    ctas.forEach(cta => this.observer.observe(cta))
  }

  // Segment-Optik wie im Pflegekosten-Rechner + aria-pressed
  highlight(buttons, value) {
    buttons.forEach(btn => {
      const on = btn.dataset.value === String(value)
      btn.classList.toggle("bg-ph24_new_blue", on)
      btn.classList.toggle("text-white", on)
      btn.classList.toggle("bg-ph24_new_gray_dark", !on)
      btn.classList.toggle("text-ph24_new_blue", !on)
      btn.classList.toggle("hover:bg-ph24_new_blue/15", !on)
      btn.setAttribute("aria-pressed", on ? "true" : "false")
    })
  }

  render() {
    const data = this.dataValue
    const formel = this.formelValue
    const pg = this.pflegegrad
    const haeuslich = this.ort !== "stationaer"
    const leistungen = data.leistungen[pg] || {}
    const zuzahlung = data.zuzahlung_tag * data.zuzahlung_max_tage
    const maxTage = data.zuzahlung_max_tage
    const belastung = `${formel.belastung} % der jährlichen Bruttoeinnahmen, bei chronisch Kranken ${formel.belastung_chronisch} %`

    this.highlight(this.ortButtonTargets, this.ort)
    this.highlight(this.pflegegradButtonTargets, pg)
    this.highlight(this.angehoerigeButtonTargets, this.angehoerige ? "ja" : "nein")
    this.angehoerigeBlockTarget.classList.toggle("hidden", !haeuslich)
    this.kkExtraTarget.classList.toggle("hidden", haeuslich)

    // --- Die eine Zahl: Zuzahlung für die Pflege -------------------------
    // Zu Hause gibt § 61 S. 3 SGB V keinen festen Eurobetrag her, deshalb Prozent.
    if (this.ort === "home") {
      this.heroValueTarget.textContent = `${formel.prozent} %`
      this.heroUnitTarget.textContent = `der Kosten + ${formel.je_verordnung} € je Verordnung, ab 18 Jahren für höchstens ${maxTage} Tage im Jahr`
      this.heroSubTarget.textContent = `Die Pflege rund um die Uhr zahlt die Krankenkasse. Ihre Zuzahlung deckelt die Belastungsgrenze bei ${formel.belastung} % der Bruttoeinnahmen.`
      if (this.hasStickyValueTarget) this.stickyValueTarget.textContent = `${formel.prozent} % der Kosten`
    } else {
      this.heroValueTarget.textContent = this.euro.format(zuzahlung)
      this.heroUnitTarget.textContent = `höchstens im Jahr (${data.zuzahlung_tag} € je Kalendertag, ab 18 Jahren für höchstens ${maxTage} Tage)`
      this.heroSubTarget.textContent = this.ort === "wg"
        ? "Die Pflege rund um die Uhr zahlt die Krankenkasse. Miete und Verpflegung der WG kommen separat dazu."
        : "Die Krankenkasse zahlt hier sogar Unterkunft und Verpflegung. Für Sie bleibt nur die Zuzahlung."
      if (this.hasStickyValueTarget) this.stickyValueTarget.textContent = `${this.euro.format(zuzahlung)} im Jahr`
    }

    // --- Pflegekasse: Geld in die Gegenrichtung ---------------------------
    const angehoerige = this.angehoerige
    const geldTyp = angehoerige ? "Pflegegeld" : "Pflegesachleistungen"
    const geld = (haeuslich && pg >= 2) ? (leistungen[geldTyp] || 0) : 0
    const verhinderung = (haeuslich && angehoerige && pg >= 2) ? Math.round(data.verhinderungspflege_jahr / 12) : 0
    const entlastung = (haeuslich && pg >= 1) ? (leistungen["Entlastungsbetrag"] || 0) : 0
    // § 45f Abs. 1 Nr. 2 SGB XI: über den Entlastungsbetrag schon ab Pflegegrad 1
    const wgZuschlag = (this.ort === "wg" && pg >= 1) ? data.wohngruppenzuschlag : 0
    const stationaer = (!haeuslich && pg >= 1) ? (leistungen["Vollstationäre Leistungen"] || 0) : 0

    this.geldRowTarget.classList.toggle("hidden", geld === 0)
    this.geldLabelTarget.textContent = geldTyp
    this.geldParagraphTarget.textContent = angehoerige ? "§ 37 SGB XI" : "§ 36 SGB XI"
    this.geldAmountTarget.textContent = this.euro.format(geld)

    this.verhinderungRowTarget.classList.toggle("hidden", verhinderung === 0)
    this.verhinderungAmountTarget.textContent = this.euro.format(verhinderung)

    this.entlastungRowTarget.classList.toggle("hidden", entlastung === 0)
    this.entlastungAmountTarget.textContent = this.euro.format(entlastung)

    this.wgZuschlagRowTarget.classList.toggle("hidden", wgZuschlag === 0)
    this.wgZuschlagAmountTarget.textContent = this.euro.format(wgZuschlag)

    this.stationaerRowTarget.classList.toggle("hidden", stationaer === 0)
    this.stationaerAmountTarget.textContent = this.euro.format(stationaer)

    const summe = geld + verhinderung + entlastung + wgZuschlag + stationaer
    this.keinAnspruchRowTarget.classList.toggle("hidden", summe > 0)
    this.pkSummeTarget.textContent = this.euro.format(summe)
    this.pkFigureTarget.textContent = this.euro.format(summe)
    // § 43 SGB XI fließt an die Einrichtung (§ 37c Abs. 3 SGB V); Sachleistungen an den Pflegedienst
    this.pkHeadingTarget.textContent = !haeuslich ? "Pflegekasse zahlt an die Einrichtung"
      : (angehoerige ? "Pflegekasse zahlt Ihnen zusätzlich" : "Pflegekasse übernimmt zusätzlich")
    this.pkUnitTarget.textContent = haeuslich ? "pro Monat, je nach Pflegegrad" : "pro Monat, wird auf die Heimkosten angerechnet"

    // --- Sie selbst: Wohnen, getrennt von der Pflege ----------------------
    const wohnumfeld = this.euro.format(data.wohnumfeld_zuschuss)
    if (this.ort === "home") {
      this.selbstTextTarget.textContent = "Ihre normale Miete und Lebenshaltung. Durch die Intensivpflege kommen keine Wohnkosten dazu."
      this.selbstNoteTarget.textContent = `Für einen nötigen Umbau (Pflegebett-Zugang, Rampe, Bad) zahlt die Pflegekasse einmalig bis zu ${wohnumfeld} (§ 40 SGB XI).`
    } else if (this.ort === "wg") {
      this.selbstTextTarget.textContent = "Miete, Nebenkosten, Verpflegung beziehungsweise Haushaltskasse und gegebenenfalls eine Betreuungsumlage der Wohngruppe."
      this.selbstNoteTarget.textContent = "Eine bundesweite oder nach Bundesländern aufgeschlüsselte Erhebung dieser Kosten gibt es nicht. Lassen Sie sich die Kosten der konkreten WG immer schriftlich aufschlüsseln."
    } else {
      this.selbstTextTarget.textContent = "Nichts. Unterkunft, Verpflegung und die betriebsnotwendigen Investitionskosten trägt hier die Krankenkasse. Der sonst übliche Heim-Eigenanteil entfällt."
      this.selbstNoteTarget.textContent = "Voraussetzung ist ein bestehender Anspruch nach § 37c SGB V. Entfällt er, weil sich der Gesundheitszustand bessert, zahlt die Krankenkasse noch sechs Monate weiter."
    }

    // --- Klartext ---------------------------------------------------------
    this.stmtKkTarget.textContent = this.ort === "stationaer"
      ? "Die Pflege rund um die Uhr zahlt die Krankenkasse. In der vollstationären Einrichtung auch noch zusätzlich Unterkunft, Verpflegung und Investitionskosten, unter Anrechnung des Leistungsbetrags der Pflegekasse (§ 37c Abs. 3 SGB V). Ein Mindestpflegegrad ist dafür nicht nötig."
      : "Die Pflege rund um die Uhr zahlt die Krankenkasse, Sie tragen nur die gesetzliche Zuzahlung. Ein Mindestpflegegrad ist nicht nötig. Entscheidend ist die ärztliche Verordnung (§ 37c SGB V)."

    this.stmtPkTarget.textContent = summe === 0
      ? "Ohne Pflegegrad zahlt die Pflegekasse nichts dazu. Die Intensivpflege selbst bleibt davon unberührt, den Pflegegrad-Antrag lohnt es sich also trotzdem parallel zu stellen."
      : (this.ort === "stationaer"
        ? `Von der Pflegekasse kommen ${this.euro.format(summe)} im Monat als Leistungsbetrag für die vollstationäre Pflege dazu (§ 43 SGB XI). Pflegegeld, Entlastungsbetrag und Verhinderungspflege setzen häusliche Pflege voraus und entfallen hier.`
        : `Von der Pflegekasse kommen rund ${this.euro.format(summe)} im Monat für Grundpflege und Alltag dazu: ${this.euro.format(geld)} ${geldTyp}${verhinderung > 0 ? `, ${this.euro.format(verhinderung)} anteilige Verhinderungspflege` : ""}, ${this.euro.format(entlastung)} Entlastungsbetrag${wgZuschlag > 0 ? ` und ${this.euro.format(wgZuschlag)} Wohngruppenzuschlag` : ""}.${verhinderung > 0 ? ` Die Verhinderungspflege erstattet die Kasse nur, wenn eine Ersatzpflege tatsächlich anfällt (bis zu ${this.euro.format(data.verhinderungspflege_jahr)} im Jahr).` : ""}`)

    this.stmtSelbstTarget.textContent = {
      home: `Selbst tragen Sie Ihre normale Miete sowie die gesetzliche Zuzahlung von ${formel.prozent} % der Kosten plus ${formel.je_verordnung} € je Verordnung, begrenzt auf die ersten ${maxTage} Tage im Jahr. Die Belastungsgrenze nach § 62 SGB V deckelt alle Zuzahlungen zusammen bei ${belastung}. Für einen nötigen Umbau gibt es einmalig bis zu ${wohnumfeld} (§ 40 SGB XI).`,
      wg: `Selbst tragen Sie Miete und Verpflegung der WG sowie die Zuzahlung von höchstens ${this.euro.format(zuzahlung)} im Jahr. Wie hoch die Wohnkosten ausfallen, hängt von der konkreten Wohngruppe ab, belastbare Vergleichszahlen gibt es dafür also nicht.`,
      stationaer: `Selbst tragen Sie nur die gesetzliche Zuzahlung von höchstens ${this.euro.format(zuzahlung)} im Jahr (§ 61 Satz 2 SGB V). Unter 18 Jahren entfällt sie; die Belastungsgrenze nach § 62 SGB V (${belastung}) kann sie weiter senken.`
    }[this.ort]

  }
}
